import { useCallback, useEffect, useRef, useState } from 'react'
import { FeatureInfoState, FeatureInfoStateMutator } from '@modules/MapScreen/states/FeatureInfoState'

import { MapClickEvent } from '../../MapScreen/interfaces/MapEvents'
import { HighlightedNotamsStateMutator } from '../../MapScreen/states/HighlightedNotamsState'
import { ScreenElementsStateMutator } from '../../MapScreen/states/ScreenElementsState'
import { AirspacesFeatureInfo } from '../components/InfoPanel/AirspacesFeatureInfo'
import { FuelFeatureInfo } from '../components/InfoPanel/FuelFeatureInfo'
import { FeatureInfoContainer } from '../components/InfoPanel/InfoPanel.styles'
import { MeteorologyLegends } from '../components/InfoPanel/MeteorologyLegends'
import { MeteorologyTooltip } from '../components/InfoPanel/MeteorologyTooltip'
import { NavaidFeatureInfo } from '../components/InfoPanel/NavaidFeatureInfo'
import { ObstacleFeatureInfo } from '../components/InfoPanel/ObstacleFeatureInfo'
import { UserGeometryFeatureInfo } from '../components/InfoPanel/UserGeometryFeatureInfo'

import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'

function InfoPanelPresenter() {
  const infoPanelRef = useRef<HTMLDivElement>(null)
  const featureInfoState = useBehaviorSubject(FeatureInfoState)

  const [showInfoPanel, setShowInfoPanel] = useState(false)

  const handleCloseInfoPanel = useCallback(() => {
    setShowInfoPanel(false)
    setTimeout(() => {
      FeatureInfoStateMutator.hide()
      HighlightedNotamsStateMutator.clear()
      ScreenElementsStateMutator.setFeatureInfoPanel(false)
    }, 300)
  }, [FeatureInfoStateMutator, ScreenElementsStateMutator])

  // Listen to ESC key press to close the dialog
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseInfoPanel()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    const mapClickSubscription = MapClickEvent.subscribe(() => {
      // The airspace panel hosts the NOTAM tab and stays open while the pilot
      // explores the map (PLA-222): it only closes through X/Esc or when another
      // feature opens its own panel in its place.
      if (FeatureInfoState.getValue().active?.type === 'airspace') return

      handleCloseInfoPanel()
    })

    return () => {
      mapClickSubscription.unsubscribe()
    }
  }, [featureInfoState, MapClickEvent])

  useEffect(() => {
    if (featureInfoState.active) {
      setShowInfoPanel(true)
      ScreenElementsStateMutator.setFeatureInfoPanel(true)
    } else {
      // The content was cleared without going through `handleCloseInfoPanel`
      // (e.g. toggling an airspace off re-selects `null`, which calls
      // `FeatureInfoStateMutator.hide()` directly). Without this, `showInfoPanel`
      // stays true and the container renders with no children — a ~10px dark
      // sliver ("barra preta") next to the map. Keep the two in sync.
      setShowInfoPanel(false)
      ScreenElementsStateMutator.setFeatureInfoPanel(false)
    }
  }, [featureInfoState])

  return (
    // Never render the container as visible without content: even with
    // `showInfoPanel` momentarily true, an absent `active` would show as an empty
    // dark bar (see PLA-216).
    <FeatureInfoContainer ref={infoPanelRef} show={showInfoPanel && !!featureInfoState.active}>
      {featureInfoState.active?.type === 'airspace' ? (
        <AirspacesFeatureInfo
          title={featureInfoState.active?.title}
          airspaceType={featureInfoState.active?.airspaceType}
          airspaceInfo={featureInfoState.active?.feature.properties}
          onClose={handleCloseInfoPanel}
        />
      ) : featureInfoState.active?.type === 'meteorology-legend' ? (
        <MeteorologyLegends
          title={featureInfoState.active.title}
          legends={featureInfoState.active.legends}
          onClose={handleCloseInfoPanel}
        />
      ) : featureInfoState.active?.type === 'meteorology-tooltip' ? (
        <MeteorologyTooltip
          title={featureInfoState.active.title}
          content={featureInfoState.active.content}
          onClose={handleCloseInfoPanel}
        />
      ) : featureInfoState.active?.type === 'fuel' ? (
        <FuelFeatureInfo
          title={featureInfoState.active.title}
          adhpCode={featureInfoState.active.adhpCode}
          providers={featureInfoState.active.providers}
          onClose={handleCloseInfoPanel}
        />
      ) : featureInfoState.active?.type === 'obstacle' ? (
        <ObstacleFeatureInfo
          title={featureInfoState.active.title}
          obstacle={featureInfoState.active.obstacle}
          onClose={handleCloseInfoPanel}
        />
      ) : featureInfoState.active?.type === 'user-geometry' ? (
        <UserGeometryFeatureInfo
          title={featureInfoState.active.title}
          feature={featureInfoState.active.feature}
          onClose={handleCloseInfoPanel}
        />
      ) : featureInfoState.active?.type === 'navaid' ? (
        <NavaidFeatureInfo
          title={featureInfoState.active.title}
          navaid={featureInfoState.active.navaid}
          onClose={handleCloseInfoPanel}
        />
      ) : null}
    </FeatureInfoContainer>
  )
}

export { InfoPanelPresenter }
